
import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {MatTabsModule} from '@angular/material/tabs';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ignoreDomainSwitchHttpStatus, SwitchDomainService} from 'ngx-auth-util-lib';
import {
  DefaultMenuItem,
  DomainType,
  HekaConfirmDialogComponent,
  InfoBoxType,
  UserDomainSummaryDTO
} from 'ngx-heka-design-lib';
import {Subscription} from 'rxjs';
import {environment} from 'src/environments/local/environment';
import {ComponentsModule} from '../components/components.module';
import {ERROR_CODES} from '../models/codes';
import {invitationDomainIdQueryParam, leftDomainNameQueryParam, userExistsQueryParam} from '../models/multi-domain';
import {CompanyDataMgmtService} from '../services/company-data-mgmt.service';
import {NotificationService} from '../services/error-handling/notification.service';
import {LoadingService} from '../services/loading/loading.service';
import {MultiDomainMgmtService} from '../services/multi-domain-mgmt.service';
import {
  MultiDomainInvitationErrorDialogComponent
} from './multi-domain-invitation-error-dialog/multi-domain-invitation-error-dialog.component';
import {MultiDomainListComponent} from './multi-domain-list/multi-domain-list.component';
import {MultiDomainStartComponent} from './multi-domain-start/multi-domain-start.component';
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {L2MultiOrgMenuComponent} from "../components/l2-navigation-without-main-menu/l2-multi-org-menu.component";

@Component({
  selector: 'app-multi-domain-mgmt',
  imports: [
    FormsModule,
    MatTabsModule,
    ComponentsModule,
    TranslateModule,
    MultiDomainListComponent,
    MultiDomainStartComponent,
    MatIcon,
    L2MultiOrgMenuComponent,
    MatButton
],
  templateUrl: './multi-domain-mgmt.component.html',
  standalone: true,
  styleUrl: './multi-domain-mgmt.component.scss'
})
export class MultiDomainMgmtComponent implements OnInit, OnDestroy {
  isRequested = false;
  domains: UserDomainSummaryDTO[]=[];
  invitations: UserDomainSummaryDTO[]=[];
  menuItems: DefaultMenuItem[] = [
    {
      route: '/multi-domain-mgmt',
      cssClass: '',
      title: this.translateService.instant('MAIN_MENU.DOMAINS'),
      hidden: false
    }
  ];

  private subscriptions = new Subscription();

  constructor(
    private translateService: TranslateService,
    private multiDomainMgmtService: MultiDomainMgmtService,
    private switchDomainService: SwitchDomainService,
    private companyDataService: CompanyDataMgmtService,
    private notificationService: NotificationService,
    private loadingService: LoadingService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.multiDomainMgmtService.getSharedMultiDomainProfile().subscribe({
        next: profile => {
          if (profile) {
            this.domains = [];
            this.invitations = [];

            if (profile.domains.length > 0) {
              profile.domains.forEach(domain => {
                if (domain.domainType == DomainType.JOINED || domain.domainType == DomainType.BLACKLISTED) {
                  this.domains.push(domain);
                } else if (domain.domainType == DomainType.INVITED) {
                  this.invitations.push(domain);
                }
              });
            }
            this.isRequested = true;
            this.checkInvitationQueryParam();
          }
        }
      })
    );

    this.route.queryParams.subscribe({
      next: params => {
        console.log(params);
        if (params[leftDomainNameQueryParam]) {
          // remove param
          this.router.navigate([], {
            queryParams: {[leftDomainNameQueryParam]: null},
            queryParamsHandling: 'merge'
          });

          this.notificationService.showSuccess(
            this.translateService.instant('MULTI_DOMAIN.LEAVE.NOTIFICATION', {
              companyName: params[leftDomainNameQueryParam]
            })
          );
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private checkInvitationQueryParam() {
    // depends on whether user exists or not
    let autoAcceptInvitation = true;
    const invitedDomainId = this.route.snapshot.queryParamMap.get(invitationDomainIdQueryParam);
    const userExistsParam = this.route.snapshot.queryParamMap.get(userExistsQueryParam);

    if (userExistsParam == 'false') {
      autoAcceptInvitation = false;
    }

    if (invitedDomainId) {
      // remove params
      this.router.navigate([], {
        queryParams: {[invitationDomainIdQueryParam]: null, [userExistsQueryParam]: null},
        queryParamsHandling: 'merge'
      });

      const foundInvitation = this.invitations.find(i => i.domainId == invitedDomainId);

      if (foundInvitation) {
        if (autoAcceptInvitation) {
          this.acceptInvitation(foundInvitation);
        }
      } else {
        const foundDomain = this.domains.find(d => d.domainId == invitedDomainId);

        if (foundDomain) {
          this.switchDomainHcp(foundDomain.domainId);
        } else {
          this.showUserInvitationError();
        }
      }
    }
  }

  acceptInvitation(invitation: UserDomainSummaryDTO) {
    this.loadingService.setLoading(true);
    this.multiDomainMgmtService.acceptInvitation(invitation.domainId).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          this.translateService.instant('MULTI_DOMAIN.START.INVITATIONS.ACCEPTED', {
            companyName: invitation.companyName
          })
        );
        this.switchDomainHcp(invitation.domainId);
      },
      error: err => {
        this.loadingService.setLoading(false);

        if (err?.status == 400) {
          const errorCode: number = err.error?.code;

          if (errorCode == ERROR_CODES.USER_ALREADY_JOINED_DOMAIN) {
            return this.switchDomainHcp(invitation.domainId);
          } else if (errorCode == ERROR_CODES.USERS_INVITATION_NOT_FOUND) {
            return this.showUserInvitationError();
          }
        }
        throw err;
      }
    });
  }

  rejectInvitation(invitation: UserDomainSummaryDTO) {
    this.loadingService.setLoading(true);
    this.multiDomainMgmtService.rejectInvitation(invitation.domainId).subscribe({
      next: () => {
        this.loadingService.setLoading(false);
        this.showRejectInvitationSuccess(invitation);
        this.multiDomainMgmtService.updateMultiDomainProfile();
      },
      error: err => {
        this.loadingService.setLoading(false);
        if (err?.status == 400) {
          const errorCode: number = err.error?.code;

          if (errorCode == ERROR_CODES.USERS_INVITATION_NOT_FOUND) {
            this.showRejectInvitationSuccess(invitation);
            return this.multiDomainMgmtService.updateMultiDomainProfile();
          }
        }
        throw err;
      }
    });
  }

  private showRejectInvitationSuccess(invitation: UserDomainSummaryDTO) {
    this.notificationService.showSuccess(
      this.translateService.instant('MULTI_DOMAIN.START.INVITATIONS.REJECTED', {
        companyName: invitation.companyName
      })
    );
  }

  private showUserInvitationError() {
    const dialogRef = this.dialog.open(MultiDomainInvitationErrorDialogComponent, {
      ...environment.dialogs.small,
    });
    dialogRef.afterClosed().subscribe({
      next: () => this.multiDomainMgmtService.updateMultiDomainProfile()
    });
  }

  showDomainCreate() {
    this.companyDataService.setOnBoardingProgress(0);
    this.router.navigate(['onboarding']);
  }

  onDomainCreationNotAllowed() {
    this.dialog.open(HekaConfirmDialogComponent, {
      ...environment.dialogs.small,
      hasBackdrop: true,
      data: {
        title: this.translateService.instant('MULTI_DOMAIN.START.CREATE_NOT_ALLOWED.TITLE'),
        content: this.translateService.instant('MULTI_DOMAIN.START.CREATE_NOT_ALLOWED.CONTENT'),
        confirmButtonText: this.translateService.instant('GENERAL.OK')
      }
    });
  }

  private switchDomainHcp(domainId: string) {
    this.loadingService.setLoading(true);
    const originalInvitations = [...this.invitations];
    this.switchDomainService.switchDomain(domainId).subscribe({
      error: err => {
        this.loadingService.setLoading(false);
        if (err?.status != ignoreDomainSwitchHttpStatus) {
          throw err;
        }
        this.checkRouteToHcp(originalInvitations);
      },
      complete: () => {
        this.loadingService.setLoading(false);
        this.checkRouteToHcp(originalInvitations);
      }
    });
  }

  private checkRouteToHcp(originalInvitations: UserDomainSummaryDTO[]) {
    this.multiDomainMgmtService.updateMultiDomainProfile();

    if (originalInvitations.length > 1) {
      return;
    }

    window.location.href = environment.links.applications.hcp;
  }

  openHcp() {
    window.location.href = environment.links.applications.hcp;
  }

  protected readonly InfoBoxType = InfoBoxType;

}
